import clsx from 'clsx'
import { type InputHTMLAttributes, type ReactNode, forwardRef } from 'react'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean
  leftAddon?: ReactNode
  rightAddon?: ReactNode
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid, leftAddon, rightAddon, className, ...rest },
  ref,
) {
  const field = (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={clsx(
        // Apple-style: white surface, hairline border, soft 12px radius, blue focus ring.
        'h-10 w-full rounded-[var(--radius)] border bg-[var(--bg)] px-3.5 text-[14px] text-[var(--text)]',
        'placeholder:text-[var(--text-3)] outline-none transition-colors',
        'focus-visible:shadow-[var(--focus-ring)] focus-visible:border-[var(--accent)]',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        invalid ? 'border-[var(--amber)]' : 'border-[var(--border)] hover:border-[var(--text-3)]',
        leftAddon && 'rounded-l-none border-l-0',
        rightAddon && 'rounded-r-none border-r-0',
        className,
      )}
      {...rest}
    />
  )

  if (!leftAddon && !rightAddon) return field

  return (
    <div className="inline-flex w-full items-stretch">
      {leftAddon ? (
        <span className="inline-flex items-center rounded-l-[var(--radius)] border border-r-0 border-[var(--border)] bg-[var(--bg-2)] px-3 text-[var(--text-2)] text-[13px]">
          {leftAddon}
        </span>
      ) : null}
      {field}
      {rightAddon ? (
        <span className="inline-flex items-center rounded-r-[var(--radius)] border border-l-0 border-[var(--border)] bg-[var(--bg-2)] px-3 text-[var(--text-2)] text-[13px]">
          {rightAddon}
        </span>
      ) : null}
    </div>
  )
})
